import { useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import sanitize from 'sanitize-html'

import { IForm } from 'core-components/classes/models/IForm'
import { generateId } from 'core-components/helpers/generateId'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import {
  calculateRedirectData,
  getCustomRoute,
  processAuthRedirect,
  redirect,
} from 'src/core/classes/helpers/authorization'
import { getAppId } from 'src/core/classes/helpers/getAppId'
import { authByOneTimeToken } from 'src/core/classes/helpers/OneTimeAuthHelper'
import {
  clearClientSecret,
  getIdentityTokens,
  getTokens,
  setClientSecret,
  setIdentityTokens,
  setTokens,
} from 'src/core/classes/helpers/StorageHelper'
import { proxyIdentity } from 'src/core/classes/helpers/UrlHelper'
import { Rest } from 'src/core/classes/http'
import { TAuthService } from 'src/core/classes/models/IAuth'
import { userApi } from 'src/core/classes/services/api/UserApi'
import {
  clearPendingHpidLogin,
  exchangeHpidFlow,
  getHpidErrorMessage,
  hasPendingHpidLogin,
  HPID_NOT_STARTED,
  IHpidError,
  takeHpidCallback,
  takeHpidReturnUrl,
  THpidCallback,
} from 'src/core/classes/services/auth/hpidV3'
import { authService } from 'src/core/classes/services/AuthService'
import VersionStoreService from 'src/core/classes/services/VersionStoreService'
import {
  CODE_NO_PROJECT_ACCESS_NEW,
  CODE_NO_PROJECT_ACCESS_OLD,
} from 'src/core/components/app/constants/error'
import { useUserStore } from 'src/core/hooks/services/useUserStore'
import { IRouteData } from 'src/core/routes/types'
import { composeURL } from 'src/core/utils/functions'
import Logger from 'src/core/utils/Logger'
import { isTokenExpired } from 'src/core/utils/Token'

export const useInitAuth = () => {
  const [authState, setAuthState] = useState<'auth' | 'login' | 'loading'>('loading')

  const ownerId = useSelector(selectAppOwnerId)
  const { getTokenApi } = useUserStore()

  const getToken = useCallback(
    async (accessToken?: string, refreshToken?: string) => {
      let tokenObj: { AccessToken: string; RefreshToken: string } = {
        AccessToken: accessToken || '',
        RefreshToken: refreshToken || '',
      }

      if (!accessToken || !refreshToken) {
        try {
          tokenObj = await getTokenApi()
        } catch (err: unknown) {
          const message = (err as Error).message
          const hasUserAccessToProject =
            !message ||
            (!message.includes(CODE_NO_PROJECT_ACCESS_NEW) &&
              !message.includes(CODE_NO_PROJECT_ACCESS_OLD))
          if (!hasUserAccessToProject) {
            return false
          }
        }
      }
      return tokenObj
    },
    [getTokenApi]
  )

  // A second initAuth run (the effect re-runs when its deps change) must wait for the same
  // exchange instead of falling through to "signed out" or a guest sign-in while it is in flight
  const hpidCompletion = useRef<Promise<string> | null>(null)
  // The effect re-runs initAuth on renders too; the stale v2 pair is dropped once, on load
  const staleClientSecretCleared = useRef(false)

  // Declared before hpidLogin: hpidLogin lists it in its dependencies
  const completeHpidLogin = useCallback(
    async (callback: THpidCallback): Promise<string> => {
      const returnUrl = takeHpidReturnUrl()

      const fail = (error: IHpidError): never => {
        // Leave /hpid-callback. After "Reload page" the user is on the login page
        // and comes back to returnUrl after signing in.
        window.history.replaceState(null, '', `${composeURL('/')}#redirectPage=${returnUrl}`)
        // Caught in CoreLoaderWrapperInner → setAuthError → full-screen ViewError
        throw new Error(getHpidErrorMessage(error))
      }

      if ('error' in callback) {
        return fail(callback.error)
      }
      if (!hasPendingHpidLogin()) {
        return fail({ errorCode: HPID_NOT_STARTED, referenceId: null })
      }
      clearPendingHpidLogin() // one start, one redemption attempt
      const tokens = await exchangeHpidFlow(callback.flow).catch(fail)
      setTokens(ownerId, tokens.AccessToken, tokens.RefreshToken)
      redirect(returnUrl)
      return tokens.AccessToken
    },
    [ownerId]
  )

  const hpidLogin = useCallback(
    async (customRoutes: IRouteData[]): Promise<string> => {
      const hpidCallback = takeHpidCallback()
      if (hpidCallback) {
        hpidCompletion.current = completeHpidLogin(hpidCallback)
      }
      if (hpidCompletion.current) {
        return hpidCompletion.current
      }

      const dataParams = window.location.search.substring(1).split('=')
      const [dataKey, dataValue] = dataParams

      if (dataKey === 'tokens') {
        const tokens = dataValue.split(',')
        const tokenObj = await getToken(tokens[0], tokens[1])

        if (tokenObj === false) {
          redirect('/registration')
          return ''
        }

        if (tokenObj.AccessToken && tokenObj.RefreshToken) {
          setTokens(ownerId, tokenObj.AccessToken, tokenObj.RefreshToken)
          redirect(window.location.pathname)
          return tokenObj.AccessToken
        }
      } else {
        if (!authService.hasToken()) {
          const hasGuestRoute = !!getCustomRoute(
            customRoutes,
            ['Guest', 'Common'],
            window.location.pathname
          )

          if (dataKey && dataValue && !hasGuestRoute) {
            const hasOneTimeToken = dataKey === 'oneTimeToken'
            const oneTimeToken = (hasOneTimeToken && dataValue) || ''
            const clientId = oneTimeToken.substring(0, oneTimeToken.length / 2)
            const secretId = oneTimeToken.substring(oneTimeToken.length / 2)
            setClientSecret(ownerId, clientId, secretId)
            try {
              const tokenObj = await getToken()
              if (tokenObj && tokenObj.AccessToken && tokenObj.RefreshToken) {
                setTokens(ownerId, tokenObj.AccessToken, tokenObj.RefreshToken)
                return tokenObj.AccessToken
              }
            } catch (e) {
              Logger.error(e)
            }
          }
        }
      }
      return ''
    },
    [completeHpidLogin, getToken, ownerId]
  )

  const login = useCallback(async (type: TAuthService) => {
    const isAuthUrl = window.location.pathname.includes(`/authentication/${type}/callback`)
    if (isAuthUrl) {
      const dataParamsString = window.location.search.substring(1)
      const paramsStrings = dataParamsString.split('&')
      const { code, state = '/' }: IForm =
        paramsStrings.reduce((params, el) => {
          const [key, value] = el.split('=')
          return { ...params, [key]: decodeURIComponent(value) }
        }, {}) || {}

      if (code) {
        try {
          const url = sanitize(proxyIdentity(`/authentication/${type}/code`))
          const response = await Rest.post({
            url,
            payload: {
              appId: getAppId(),
              code,
              redirectUri: sanitize(window.location.origin + `/authentication/${type}/callback`),
            },
          })

          if (response.AccessToken && response.RefreshToken) {
            setIdentityTokens(response.AccessToken, response.RefreshToken)
            await authService.exchangeIdentityToken(response.AccessToken)
            window.location.href = sanitize(`${window.location.origin}${state}`)
          } else {
            throw new Error('Empty authorization request response')
          }
        } catch (err: unknown) {
          Logger.error(`${(err as Error).message}`)
        }
      }
    }
  }, [])

  const initAuth = useCallback(
    async (availableGuestAuthorization: boolean, customRoutes: IRouteData[]) => {
      authService.init()
      // v2 HP ID pairs left in storage by older logins. The one-time token paths below
      // write a pair again when they need one.
      if (!staleClientSecretCleared.current) {
        staleClientSecretCleared.current = true
        clearClientSecret(ownerId)
      }

      await authByOneTimeToken()
      await hpidLogin(customRoutes)

      const locationPaths = window.location.pathname.split('/')
      const typeIndex = locationPaths.indexOf('authentication')
      if (typeIndex > -1) {
        const type = locationPaths[typeIndex + 1]
        await login(type as TAuthService)
      }

      if (authService.hasToken()) {
        const [identityToken] = getIdentityTokens()
        const [oldToken] = getTokens()
        const token = identityToken || oldToken

        if (token && isTokenExpired(token)) {
          await authService.refreshToken()
        } else if (identityToken) {
          await authService.exchangeIdentityToken(identityToken)
        }
        const { error: initStorageError } = await VersionStoreService.init()

        if (initStorageError) {
          throw new Error(`Can't initialize storage service. Check internet connection`)
        }

        setAuthState('auth')
      } else if (availableGuestAuthorization) {
        const nick = generateId('guest-user')
        const loginResult = await userApi.signInGuest(nick)
        if (loginResult) {
          const { hash, pathname } = window.location

          const { redirectUrl } = calculateRedirectData({
            hash,
            pathname,
            loginResult,
          })
          processAuthRedirect(redirectUrl)
        }
      } else {
        // For login or unauth pages
        setAuthState('login')
        authService.clearToken()
      }
    },
    [hpidLogin, login, ownerId]
  )

  return { initAuth, authState }
}
